/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { ExpandedWarningSections, WarningsFiltersState } from './types';
import type { FilterState } from '../../components/FilterList/filter-hooks';
import { initialFilters } from '../../components/FilterList/filter-utils';

export const defaultExpandedWarningSections: ExpandedWarningSections = {
  TODO: false,
  DRAFT: false,
  PUBLISHED: true,
  EXPIRED: false,
};

export const initialWarningFilterPanelState = {
  ...initialFilters,
  expandedSections: defaultExpandedWarningSections,
  hoveredWarningIds: [],
  hiddenWarningIds: [],
};

export const initialState: WarningsFiltersState = {};

const slice = createSlice({
  initialState,
  name: 'warningsFilters',
  reducers: {
    setWarningFilters: (
      draft: Draft<WarningsFiltersState>,
      action: PayloadAction<{
        filterState: Partial<FilterState>;
        panelId?: string;
        systemUpdate: boolean;
      }>,
    ) => {
      const { filterState, panelId } = action.payload;
      if (panelId) {
        const currentPanelState =
          draft[panelId] || initialWarningFilterPanelState;
        draft[panelId] = {
          ...currentPanelState,
          ...filterState,
          expandedSections: currentPanelState.expandedSections,
        };
      }
    },
    setExpandedSectionsForPanel: (
      draft: Draft<WarningsFiltersState>,
      action: PayloadAction<{
        panelId: string;
        expandedSections: ExpandedWarningSections;
        systemUpdate?: boolean;
      }>,
    ) => {
      const currentPanelState =
        draft[action.payload.panelId] || initialWarningFilterPanelState;
      draft[action.payload.panelId] = {
        ...currentPanelState,
        expandedSections: action.payload.expandedSections,
      };
    },
    setHoveredWarningIdsForPanel: (
      draft: Draft<WarningsFiltersState>,
      action: PayloadAction<{
        panelId: string;
        warningIds?: string[];
      }>,
    ) => {
      const currentPanelState =
        draft[action.payload.panelId] || initialWarningFilterPanelState;
      draft[action.payload.panelId] = {
        ...currentPanelState,
        hoveredWarningIds: action.payload.warningIds ?? [],
      };
    },
    setHiddenWarningIdsForPanel: (
      draft: Draft<WarningsFiltersState>,
      action: PayloadAction<{
        panelId: string;
        warningIds?: string[];
      }>,
    ) => {
      const currentPanelState =
        draft[action.payload.panelId] || initialWarningFilterPanelState;
      draft[action.payload.panelId] = {
        ...currentPanelState,
        hiddenWarningIds: action.payload.warningIds ?? [],
      };
    },
  },
});

export const {
  reducer: warningsFiltersReducer,
  actions: warningsFiltersActions,
} = slice;
